import { getRandomIntInclusive } from '@application/utils';
import { useTheme } from '@emotion/react';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { BsPersonFill } from 'react-icons/bs';
import { MdLanguage, MdLocationOn } from 'react-icons/md';
import { RiUserStarLine } from 'react-icons/ri';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { Link } from 'react-router-dom';
import { Button, PlaceholderImage, SKELETON_IMAGE, SkeletonBox } from '..';
import { IconScaleStars } from '../InputStarsField/styled';
import * as Styled from './styled';
import type { CardItemProProps } from './types';

export { CardItemProContainer, CardItemProWrapper } from './styled';
export * from './types';

export const CardItemPro: FC<CardItemProProps> = (props) => {
  const {
    image,
    name,
    description,
    languages,
    services,
    location,
    center,
    distance,
    hasBooking = false,
    loading = false,
    link,
    reviewsList,
    lazyLoadImage = true,
    ...rest
  } = props;

  const { translate } = useTranslation();
  const theme = useTheme();
  const skeletons = useMemo(() => {
    return {
      title: { width: '100%', height: 36.5 },
      descriptionTop: { width: '100%', height: 43, display: 'block', marginBottom: 10 },
      descriptionBottom: { width: getRandomIntInclusive(150, 400), height: 21.5 },
      values: Array(2)
        .fill(undefined)
        .map(() => ({ width: getRandomIntInclusive(140, 180), height: 25 })),
      button: { width: 200, height: 40 },
    };
  }, []);

  // TODO: Abstract this component
  if (loading)
    return (
      <Styled.Wrapper>
        <Styled.Image src={SKELETON_IMAGE} />
        <Styled.Main hasImage>
          <Styled.ContentWrapper>
            <Styled.Title>
              <SkeletonBox style={{ ...skeletons.title }} />
            </Styled.Title>
            <Styled.Description>
              <SkeletonBox style={{ ...skeletons.descriptionTop }} />
              <SkeletonBox style={{ ...skeletons.descriptionBottom }} />
            </Styled.Description>
          </Styled.ContentWrapper>
          <Styled.SideWrapper>
            {skeletons.values.map((value, index) => (
              <Styled.InfoWrapper key={index}>
                <SkeletonBox style={{ ...value }} />
              </Styled.InfoWrapper>
            ))}
            <SkeletonBox style={{ ...skeletons.button }} />
          </Styled.SideWrapper>
        </Styled.Main>
      </Styled.Wrapper>
    );

  return (
    <Link to={link as any} className="a-reset">
      <Styled.Wrapper {...rest}>
        {image ? (
          <>
            {lazyLoadImage ? (
              <LazyLoadComponent
                placeholder={
                  <PlaceholderImage style={{ height: '100%', width: 200, borderRadius: 10 }} />
                }
              >
                <Styled.Image src={image} alt="pro-profile-image" />
              </LazyLoadComponent>
            ) : (
              <Styled.Image src={image} alt="pro-profile-image" />
            )}
          </>
        ) : null}
        <Styled.Main hasImage={!!image}>
          <Styled.ContentWrapper>
            <Styled.Title>{name}</Styled.Title>
            {!!description && (
              <Styled.Description dangerouslySetInnerHTML={{ __html: description }} />
            )}
            {!!services?.length && (
              <Styled.Services>
                <Styled.ServicesSpan>
                  {translate('header.loggued.services')}
                  {': '}
                </Styled.ServicesSpan>
                {services.join(', ')}
              </Styled.Services>
            )}
          </Styled.ContentWrapper>
          <Styled.SideWrapper>
            {location && (
              <Styled.InfoWrapper>
                <Styled.InfoIcon>
                  <MdLocationOn fill={theme.color.blue.default} />
                </Styled.InfoIcon>
                <div>
                  {distance && <span>{distance}, </span>}
                  {location}
                </div>
              </Styled.InfoWrapper>
            )}
            {center && (
              <Styled.InfoWrapper>
                <Styled.InfoIcon>
                  <BsPersonFill fill={theme.color.blue.default} />
                </Styled.InfoIcon>
                {center}
              </Styled.InfoWrapper>
            )}
            {languages && (
              <Styled.InfoWrapper>
                <Styled.InfoIcon>
                  <MdLanguage fill={theme.color.blue.default} />
                </Styled.InfoIcon>
                {languages}
              </Styled.InfoWrapper>
            )}
            {!!reviewsList && (
              <Styled.ReviewWrapper>
                <Styled.InfoIcon>
                  <RiUserStarLine fill={theme.color.blue.default} />
                </Styled.InfoIcon>
                <IconScaleStars amount={reviewsList?.average ?? 0} />
                <Styled.ReviewText>({reviewsList?.amount})</Styled.ReviewText>
              </Styled.ReviewWrapper>
            )}
            {hasBooking ? (
              <Button color="blue">{translate('common.buttons.book')}</Button>
            ) : (
              <Button color="lightBlue">{translate('common.buttons.contactPro')}</Button>
            )}
          </Styled.SideWrapper>
        </Styled.Main>
      </Styled.Wrapper>
    </Link>
  );
};
